/*
  FILE: passport.js

  PURPOSE:
  Acts as a global user identity manager. Once a user logs in, this file 
  retrieves their authentication and profile info (e.g. id, first name, last name) 
  and stores it for global access throughout the app.

  The Passport provider should be imported and wrapped around the main app 
  in App.js to ensure user data is available everywhere.
*/

import React, { createContext, useContext, useEffect, useState } from 'react';
import supabase from './config/supabaseClient';

const PassportContext = createContext();

export const PassportProvider = ({ children }) => {
  const [user, setUser] = useState(null);       // Supabase auth user
  const [profile, setProfile] = useState(null); // User profile from 'profiles' table
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
      } else {
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    };

    const fetchProfile = async (userId) => {
      let { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.warn('Primary profile fetch by user_id failed — trying fallback by id');
        ({ data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle());
      }

      if (error) {
        console.error('❌ Final error fetching profile:', error);
      } else {
        setProfile(data);
      }

      setLoading(false);
    };


    fetchSession();

    // Listen to login/logout changes
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
      } else {
        setUser(null);
        setProfile(null);
      }
    });

    return () => {
      listener?.subscription.unsubscribe();
    };
  }, []);

  return (
    <PassportContext.Provider value={{ user, profile, loading }}>
      {children}
    </PassportContext.Provider>
  );
};

export const usePassport = () => useContext(PassportContext);

export const usePassportUserId = () => {
  const { user } = usePassport();
  return user?.id || null;
};
